import { ArrowRightIcon, ShieldTickIcon, VaultsIcon } from 'assets/icons';
import { CreateVaultSteps } from 'common/enums';
import CypressIds from 'cypressIds';
import { classnames } from 'helpers/utils';
import {
  billingLabel,
  formatPrice,
  renewalLabel,
  sizeLabel,
} from '../../plan-format';
import { VaultNewStateType } from '../../vault-new-reducer';
import styles from './wizard-footer.module.scss';

type WizardFooterProps = {
  newVaultState: VaultNewStateType;
  loading: boolean;
  notifyNextAction: () => void;
};

const primaryLabel = (
  step: CreateVaultSteps,
  loading: boolean,
  plan: StoragePlanType | null,
) => {
  if (step !== CreateVaultSteps.confirmation) return 'Continue';
  if (loading) return 'Starting checkout…';

  return plan
    ? `Continue to payment · ${formatPrice(plan.price, plan.currency)}`
    : 'Continue to payment';
};

// Sticky summary bar: what the vault is so far on the left, what it costs and
// the way forward on the right. Present on every step so the running total
// never disappears; `Back` lives next to the page title, as in the design.
//
// The CTA is a native button rather than `common/button` because the shared
// `Primary` style is built on the old cool `$brand600` (#1c7387) and this
// screen follows the GS design-system brand (#034d51).
const WizardFooter: React.FC<WizardFooterProps> = ({
  newVaultState,
  loading,
  notifyNextAction,
}) => {
  const { step, planSelected, vault, coverPreviewUrl } = newVaultState;

  const hasName = vault.title.trim().length > 0;
  const isDetailsStep = step === CreateVaultSteps.information;

  const isNextDisabled =
    loading ||
    (step === CreateVaultSteps.plan && !planSelected) ||
    (isDetailsStep && !hasName);

  const showVaultSummary = () => (
    <div className={styles.vault}>
      <div className={styles.avatar}>
        {coverPreviewUrl ? (
          <img src={coverPreviewUrl} alt="" />
        ) : (
          <VaultsIcon className={styles.avatarPlaceholder} />
        )}
      </div>

      <div className={styles.vaultText}>
        {/* Until the vault is named, the footer prompts for it instead of
            showing a placeholder title. */}
        <span
          className={classnames(styles.vaultTitle, {
            [styles.vaultTitlePrompt]: isDetailsStep && !hasName,
          })}
        >
          {hasName ? vault.title.trim() : 'Name your vault to continue'}
        </span>

        {isDetailsStep && (
          <span className={styles.vaultStorage}>
            {`${vault.contributors.length} collaborator${
              vault.contributors.length === 1 ? '' : 's'
            }`}
          </span>
        )}

        {planSelected && (
          <div className={styles.vaultMeta}>
            <span className={styles.vaultStorage}>
              {sizeLabel(planSelected.storageGb)}
            </span>
            {planSelected.kind === 'long_term' && (
              <span className={styles.badge}>
                Yours forever
                <ShieldTickIcon className={styles.badgeIcon} />
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );

  const showPrice = () => {
    if (!planSelected) return null;

    // On the review step the amount already sits inside the CTA, so the footer
    // only carries the renewal caveat.
    if (step === CreateVaultSteps.confirmation) {
      return (
        <span className={styles.priceNote}>
          {planSelected.interval
            ? `Renews ${renewalLabel(planSelected)}`
            : 'One payment — no renewals'}
        </span>
      );
    }

    return (
      <div className={styles.price}>
        <div className={styles.subtotal}>
          <span className={styles.amount}>
            {formatPrice(planSelected.price, planSelected.currency)}
          </span>
          <span className={styles.period}>{billingLabel(planSelected)}</span>
        </div>
        <span className={styles.priceNote}>
          You&apos;ll review before paying
        </span>
      </div>
    );
  };

  return (
    <div className={styles.footer}>
      <div className={styles.inner}>
        {showVaultSummary()}

        <div className={styles.actions}>
          {showPrice()}

          <button
            type="button"
            className={styles.nextButton}
            onClick={notifyNextAction}
            disabled={isNextDisabled}
            data-cy={CypressIds.createVaultButton}
          >
            {primaryLabel(step, loading, planSelected)}
            <ArrowRightIcon className={styles.nextIcon} />
          </button>
        </div>
      </div>
    </div>
  );
};

export { WizardFooter };
